import { PROJECT_LOGOS } from "@/assets/static/images";
import Project from "@/types/Project";

export const solarMonitoring: Project = {
        logo: PROJECT_LOGOS.solarMonitoring,
        colors: ["#201800", "#FFC429"],
        name: "Solar Monitoring App",
        description:
            "A high-fidelity industrial monitoring application for real-time renewable energy telemetry. It leverages custom Canvas rendering for sub-millisecond data visualization and a robust BLE (Bluetooth Low Energy) stack to handle high-frequency inverter updates without packet loss. Built on a Clean MVVM architecture for maximum reliability in mission-critical field environments.",
        skills: ["Mobile Infrastructure"],
        tech_stack_ids: ["kotlin", "jetpack-compose", "android-architecture", "coroutines-flow", "bluetooth"],
        company_id: "zarin-electronic",
        started_date: "May 25’",
        ended_date: "Jun 25’",
        highlights: [
            "Custom-built Canvas rendering engine delivering 60fps real-time data visualization of hardware telemetry",
            "High-resilience BLE stack with automated reconnection logic and packet-integrity verification",
            "Clean Architecture ensuring 100% testability of critical data conversion and business logic",
            "Advanced telemetry components (Gauges, Charts) engineered from scratch for performance and clarity",
            "Resource-optimized background service for continuous monitoring without excessive battery drain",
        ],
        accessibility: "private",
        case_study: {
            slug: "solar-monitoring-app",
            project_name: "Solar Monitoring App",
            preview_video_src: "/case_studies/solar_monitoring/scenes/scene_3",
            thumbnail_image: PROJECT_LOGOS.solarMonitoring,
            hero_image: "/case_studies/solar_monitoring/hero_image.webp",
            hero_title: "Precision Engineering: Real-Time Industrial IoT Monitoring",
            hero_subtitle:
                "Visualizing hardware-critical telemetry through custom high-performance Canvas rendering and resilient BLE integration.",
            overview: {
                role: "Android Developer (Contract)",
                team_size: "Solo Developer",
                platforms: ["Android"],
                summary:
                    "<strong>The Problem:</strong> Industrial solar monitoring required real-time (20Hz+) data telemetry on legacy mobile hardware, causing standard rendering libraries to choke. <strong>The Solution:</strong> I built a high-performance Android telemetry app using custom Canvas-based rendering, spring physics animations, and a reactive BLE layer with channels. <strong>The Result:</strong> Delivered stable 60fps telemetry dashboards with a sub-80ms 'hardware-to-screen' latency budget and 100% automatic connection recovery.",
            },
            tech_details: {
                title: "Technical Excellence",
                description:
                    "Industrial monitoring requires a higher standard of reliability than consumer apps. Failure to report data can lead to hardware damage.",
                architecture_highlights: [
                    "<strong>Custom Canvas Renderers:</strong> Bypassing the view system for direct telemetry drawing, ensuring 60fps on low-end devices.",
                    "<strong>Reactive BLE Layer:</strong> Using Kotlin Coroutines and Channels to buffer and process high-frequency hardware packets.",
                    "<strong>Strict Clean Architecture:</strong> Isolating the Bluetooth protocol logic from the UI to ensure 100% unit test coverage.",
                    "<strong>Physics-Based Animation:</strong> Using spring physics for gauges to provide natural, non-jittery feedback for volatile data.",
                ],
            },
            gallery: [
                {
                    type: "video",
                    src: "/case_studies/solar_monitoring/scenes/scene_1",
                    caption:
                        "Automated hardware discovery: physics-based animations establishing a clear visual link between the app and the physical inverter.",
                    muted: true,
                    autoplay: true,
                    loop: true,
                },
                {
                    type: "video",
                    src: "/case_studies/solar_monitoring/scenes/scene_3",
                    caption:
                        "The rendering engine: custom-built gauges and charts processing 20+ telemetry updates per second with zero jank.",
                    muted: true,
                    autoplay: true,
                    loop: true,
                },
            ],
            architecture_and_scale: {
                title: "Industrial Trade-offs",
                description: "Balancing the need for real-time feedback with the constraints of mobile hardware.",
                trade_offs: [
                    {
                        decision: "Custom Canvas vs. Library Charts",
                        rationale:
                            "Third-party libraries struggled with the frequency of updates (20Hz+). I built a custom Canvas-based system to own the draw calls, resulting in a 70% reduction in CPU overhead during active monitoring.",
                    },
                    {
                        decision: "StateFlow vs. SharedFlow for Telemetry",
                        rationale:
                            "I used StateFlow for the 'Latest Reading' to ensure the UI always shows the current state, but utilized SharedFlow for 'Event Alerts' to ensure that every critical system error is processed exactly once by the observer.",
                    },
                ],
            },
            infrastructure: {
                title: "Reliability Engineering",
                description: "Ensuring the app remains a trustworthy tool in the field.",
                details: [
                    "<strong>Packet Integrity:</strong> Custom checksum verification for incoming Bluetooth packets to filter out industrial interference.",
                    "<strong>Automated Recovery:</strong> Exponential backoff strategy for Bluetooth reconnection after signal loss in metallic environments.",
                    "<strong>Battery Profiling:</strong> Targeted optimizations reducing BLE radio-on time by 25% without sacrificing data latency.",
                ],
            },
            results: [
                {
                    metric: "Visual Performance",
                    value: "60 FPS Stable",
                    description:
                        "Custom Canvas renderers eliminated UI jank, even during 20Hz hardware telemetry bursts.",
                    colors: ["#5ED3B3", "#4FA9D8", "#6B8CF5"],
                },
                {
                    metric: "Latency",
                    value: "< 80ms",
                    description: "Optimized the entire pipeline from BLE packet arrival to pixels on screen.",
                    colors: ["#FFB347", "#FF7A7A", "#D65BFF"],
                },
                {
                    metric: "Recovery Rate",
                    value: "100%",
                    description:
                        "Resilient BLE stack successfully recovered from all signal drops during field testing.",
                    colors: ["#2BD9A4", "#1FBF89", "#2A9D8F"],
                },
            ],
            challenge: {
                title: "The Problem & Constraints",
                description:
                    "Inverters emit high-frequency telemetry in environments with significant electrical interference. The challenge was to maintain a stable connection and render data smoothly on various hardware.",
                points: [
                    "<strong>Problem:</strong> Standard charting libraries caused UI freeze during high-frequency Bluetooth bursts.",
                    "<strong>Constraint:</strong> The app must run on legacy Android tablets used by on-site technicians.",
                    "<strong>Performance:</strong> Achieving a sub-100ms 'Hardware-to-Screen' latency budget.",
                    "<strong>Reliability:</strong> Ensuring the Bluetooth connection survives the electrical noise typical in solar installations.",
                ],
            },
            conclusion: {
                title: "Conclusion & Reflection",
                content:
                    "The Solar Monitoring app was a masterclass in 'Performance as a Feature'. It proved that for industrial tools, the technical integrity of the data visualization is directly tied to the user's trust in the hardware. By owning the rendering pipeline, I delivered a product that felt like a professional diagnostic tool, not just a mobile app.",
                key_takeaways: [
                    "<strong>Performance is UX:</strong> In telemetry apps, frame-rate and latency ARE the user experience.",
                    "<strong>Bypass the Bloat:</strong> Sometimes the SDK is too slow—don't be afraid to go down to the Canvas.",
                    "<strong>Build for the Field:</strong> Real-world environments are messy; your code needs to be resilient to noise and signal loss.",
                ],
            },
        },
    }
